/**
 * Meta Pixel + Google Analytics 4 (gtag) loader for ad-conversion tracking.
 *
 * Both are fully optional and no-op until the matching env var is set — same pattern as
 * Sentry in instrument.ts. Without this wired up, Meta/Google Ads can still serve traffic, but
 * they can't learn which visitors actually bought anything, which cripples campaign
 * optimization (lookalike audiences, budget allocation, "Advantage+"/Smart Bidding) from day
 * one of running paid ads.
 *
 *   REACT_APP_META_PIXEL_ID       — Meta (Facebook/Instagram) Pixel ID, from Meta Events Manager
 *   REACT_APP_GA4_MEASUREMENT_ID  — Google Analytics 4 Measurement ID (format: G-XXXXXXXXXX)
 *
 * Set either/both in frontend/.env.production before building for a real deploy.
 */

import { API_BASE_URL, BUSINESS_ID } from './config/api';
import { getFbc, getFbp } from './utils/attribution';

const META_PIXEL_ID = (process.env.REACT_APP_META_PIXEL_ID || '').trim();
const GA4_ID = (process.env.REACT_APP_GA4_MEASUREMENT_ID || '').trim();

declare global {
  interface Window {
    fbq?: ((...args: unknown[]) => void) & { callMethod?: unknown; queue?: unknown[]; loaded?: boolean; version?: string; push?: unknown };
    _fbq?: unknown;
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

function loadMetaPixel(pixelId: string) {
  if (typeof window === 'undefined' || typeof document === 'undefined' || window.fbq) return;

  // Standard Meta Pixel bootstrap snippet (functionally identical to Meta's own embed code).
  const w = window;
  const n: Window['fbq'] = function fbq(...args: unknown[]) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const self = n as any;
    if (self.callMethod) {
      self.callMethod.apply(self, args);
    } else {
      self.queue.push(args);
    }
  };
  if (!w._fbq) w._fbq = n;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (n as any).push = n;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (n as any).loaded = true;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (n as any).version = '2.0';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (n as any).queue = [];
  w.fbq = n;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);

  w.fbq('init', pixelId);
  w.fbq('track', 'PageView');
}

function loadGa4(measurementId: string) {
  if (typeof window === 'undefined' || typeof document === 'undefined' || window.gtag) return;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer!.push(args);
  };
  window.gtag('js', new Date());
  window.gtag('config', measurementId);
}

/** Call once at app startup (see index.tsx). No-ops until IDs are configured. */
export function initAdPixels() {
  if (META_PIXEL_ID) loadMetaPixel(META_PIXEL_ID);
  if (GA4_ID) loadGa4(GA4_ID);
}

export interface PurchaseConversionItem {
  id?: string;
  name?: string;
  quantity?: number;
  price?: number;
}

export interface PurchaseConversionInput {
  orderId: string;
  value: number;
  currency?: string;
  items?: PurchaseConversionItem[];
}

/**
 * Fire the Purchase/conversion event both ad platforms need in order to learn who converts and
 * optimize ad spend toward more of the same. Call once per completed order (see
 * CheckoutSuccess.js) — safe no-op if pixels aren't configured, and safe to call even if the
 * scripts are still loading (fbq/gtag queue calls internally).
 */
export function trackPurchaseConversion({ orderId, value, currency = 'AUD', items = [] }: PurchaseConversionInput) {
  if (typeof window === 'undefined') return;

  if (META_PIXEL_ID && window.fbq) {
    // eventID must match the eventId the backend sends to the Conversions API for the same
    // order (see meta-conversions-api.service.ts) so Meta dedupes the browser + server event
    // instead of counting the purchase twice.
    window.fbq(
      'track',
      'Purchase',
      {
        value,
        currency,
        content_ids: items.map((i) => i.id).filter(Boolean),
        content_type: 'product',
        num_items: items.reduce((n, i) => n + (i.quantity || 1), 0),
      },
      { eventID: orderId }
    );
  }

  if (GA4_ID && window.gtag) {
    window.gtag('event', 'purchase', {
      transaction_id: orderId,
      value,
      currency,
      items: items.map((i) => ({
        item_id: i.id,
        item_name: i.name,
        quantity: i.quantity,
        price: i.price,
      })),
    });
  }
}

function generateEventId(): string {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

interface FunnelEventInput {
  contentIds: string[];
  currency?: string;
  value?: number;
}

/**
 * Fire a funnel event (ViewContent/AddToCart/InitiateCheckout) on both the browser Pixel and,
 * via the backend, the Conversions API — same pattern as trackPurchaseConversion, but for the
 * steps before purchase. These feed Meta's optimization richer signal earlier in the funnel
 * (Advantage+ needs full-funnel volume, not just purchases, to learn who's likely to convert)
 * and let the Analytics dashboard show view→cart→checkout→purchase drop-off per campaign.
 */
function trackFunnelEvent(eventName: 'ViewContent' | 'AddToCart' | 'InitiateCheckout', { contentIds, currency = 'AUD', value }: FunnelEventInput) {
  if (typeof window === 'undefined') return;

  const eventId = generateEventId();

  if (META_PIXEL_ID && window.fbq) {
    window.fbq('track', eventName, { content_ids: contentIds, content_type: 'product', currency, value }, { eventID: eventId });
  }
  if (GA4_ID && window.gtag) {
    const ga4Name = eventName === 'ViewContent' ? 'view_item' : eventName === 'AddToCart' ? 'add_to_cart' : 'begin_checkout';
    window.gtag('event', ga4Name, { currency, value, items: contentIds.map((id) => ({ item_id: id })) });
  }

  if (META_PIXEL_ID && BUSINESS_ID) {
    fetch(`${API_BASE_URL}/analytics/meta-conversion`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        businessId: BUSINESS_ID,
        eventName,
        eventId,
        contentIds,
        currency,
        value,
        fbc: getFbc(),
        fbp: getFbp(),
        eventSourceUrl: window.location.href,
      }),
    }).catch(() => {
      // Non-fatal — the browser Pixel event above already fired.
    });
  }
}

export function trackViewContent(input: FunnelEventInput) {
  trackFunnelEvent('ViewContent', input);
}

export function trackAddToCart(input: FunnelEventInput) {
  trackFunnelEvent('AddToCart', input);
}

export function trackInitiateCheckout(input: FunnelEventInput) {
  trackFunnelEvent('InitiateCheckout', input);
}
